import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { visuallyHidden } from '@mui/utils';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import Chip from '@mui/material/Chip';
import OrderService from '../../../services/order.service';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Link } from 'react-router-dom';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import OrderDetails from './OrderDetails';

const headCells = [
  {
    id: 'number',
    numeric: false,
    label: 'Numéro',
  },
  {
    id: 'order_status',
    numeric: false,
    label: 'Statut',
  },
  {
    id: 'status_withdraw',
    numeric: false,
    label: 'Statut retrait',
  },
  {
    id: 'client',
    numeric: false,
    label: 'Client',
  },
  {
    id: 'amount',
    numeric: true,
    label: 'Montant (FCFA)',
  },
  {
    id: 'createdAt',
    numeric: false,
    label: 'Date',
  },
  {
    id: 'actions',
    numeric: false,
    label: '',
  },
];

export default function List() {
  const [orders, setOrders] = React.useState([]);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('createdAt');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [totalOrders, setTotalOrders] = React.useState(0);
  React.useEffect(() => {
    fetchOrders();
  }, []);
  const fetchOrders = async (limit = rowsPerPage, pageNum = 1) => {
    console.log("------ fetchOrders : ", limit, pageNum, searchQuery, statusFilter);
    try {
      const response = await OrderService.list(limit, pageNum, searchQuery, statusFilter);
      setOrders(response.orders);
      console.log("------ response.orders : ", response.orders);
      console.log("------ orders : ", orders);
      setTotalOrders(response.pagination.total);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des commandes',
        severity: 'error'
      });
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    await fetchOrders(rowsPerPage, newPage + 1);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    try {
      const response = await OrderService.list(newRowsPerPage, 1);
      setOrders(response.orders);
      setTotalOrders(response.pagination.total);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des commandes',
        severity: 'error'
      });
    }
  };

  const handleSearch = async (event) => {
    console.log("------ handleSearch : ", event.target.value);
    setSearchQuery(event.target.value);
    setPage(0);
    
    // Utiliser un délai de 2 secondes avant de lancer la recherche
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    window.searchTimeout = setTimeout(async () => {
      await fetchOrders(rowsPerPage, 1, event.target.value);
    }, 500);
  };

  const handleShowDetails = async (order_id) => {
    console.log("------ handleShowDetails : ", order_id);
    try {
      const orderDetails = await OrderService.show(order_id);
      setSelectedOrder(orderDetails);
      setDetailsOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des détails de la commande',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };


  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa' }}>
      <Paper sx={{ width: '100%', mb: 2, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
        <Toolbar sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '20px', fontWeight: 'bold' }}>
            Commandes
          </Typography>
          <Box sx={{ p: 2 }}>
            <FormControl size="small" sx={{ mr: 2, minWidth: 200 }}>
              <Select
                value={statusFilter || ''}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  }
                }}
              >
                <MenuItem value="">Tous les statuts</MenuItem>
                <MenuItem value="success">
                  <Chip 
                    label="Succès" 
                    size="small"
                    color="success"
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
                <MenuItem value="pending">
                  <Chip
                    label="En attente"
                    size="small" 
                    color="warning"
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
                <MenuItem value="failed">
                  <Chip
                    label="Échoué"
                    size="small"
                    color="error" 
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              size='small'
              value={searchQuery}
              onKeyUp={handleSearch}
              onChange={handleSearch}
              placeholder="Rechercher..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} fontSize='small' />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                }
              }}
            />
            <Button
              component={Link}
              to="/backoffice/check-transactions"
              startIcon={<CompareArrowsIcon />}
              variant="contained"
              sx={{ ml: 2 }}
            >
              Vérifier Transactions
            </Button>
          </Box>
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>{row.number}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.order_status} 
                      color={getStatusColor(row.order_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.status_withdraw} 
                      color={getStatusColor(row.status_withdraw)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{row.client.fullname}</TableCell>
                  <TableCell align="right">
                    {Number(row.amount).toLocaleString()} FCFA
                  </TableCell>
                  <TableCell>
                    {new Date(row.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleShowDetails(row.id)}>
                      <InfoIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalOrders}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      <OrderDetails
        order={selectedOrder}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}